import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Star, Trophy, Lock } from "lucide-react";

const BONUS_DEFS = [
  { key: "champion",          label: "Champion",                  pts: 15, hint: "Who lifts the trophy?",        kind: "country" },
  { key: "runner_up",         label: "Runner-up",                 pts: 8,  hint: "Who loses the final?",          kind: "country" },
  { key: "golden_boot",       label: "Golden Boot",               pts: 10, hint: "Tournament's top scorer",       kind: "player" },
  { key: "dark_horse",        label: "Dark Horse",                pts: 8,  hint: "Furthest progressing team",      kind: "country" },
  { key: "golden_glove",      label: "Golden Glove",              pts: 6,  hint: "Best goalkeeper of the tournament", kind: "player" },
  { key: "final_goals_ou",    label: "Final Goals (O/U 2.5)",     pts: 5,  hint: "Goals scored in the final",     kind: "ou" },
];

export default function Bonus() {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState({});
  const [picks, setPicks] = useState({});
  const [results, setResults] = useState({});
  const [picksMeta, setPicksMeta] = useState({});
  const [options, setOptions] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [cfg, mine, res, opts] = await Promise.all([
        api.get("/tournament/config"),
        api.get("/bonus/predictions/mine"),
        api.get("/bonus/results"),
        api.get("/bonus/options"),
      ]);
      setConfig(cfg.data);
      const map = {};
      const meta = {};
      mine.data.forEach(b => { map[b.type] = b.value; meta[b.type] = b; });
      setPicks(map);
      setPicksMeta(meta);
      setResults(res.data || {});
      setOptions(opts.data || {});
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const isLocked = config.is_locked;

  const save = async (key) => {
    const value = (picks[key] || "").trim();
    if (!value) { toast.error("Pick a value first"); return; }
    try {
      await api.post("/bonus/predictions", { type: key, value });
      toast.success("Bonus pick saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-screen-2xl mx-auto px-6 md:px-10 pt-10 pb-20 relative z-10">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
          <div className="text-[10px] tracking-[0.3em] uppercase text-lavender mb-3">High-Stakes Picks</div>
          <h1 className="font-display text-5xl sm:text-6xl tracking-tight uppercase leading-none">
            Tournament <span className="text-purple">Bonus</span>
          </h1>
          <p className="text-white/60 mt-3 text-sm max-w-2xl">
            One shot. Up to <span className="text-lavender font-bold">52 bonus points</span>.
            {isLocked && " Locked — results pending."}
          </p>
        </motion.div>

        {isLocked && (
          <div className="mb-6 p-4 rounded-xl bg-lavender/5 border border-lavender/30 text-lavender flex items-center gap-3" data-testid="bonus-locked-banner">
            <Lock size={16}/>
            <div className="text-xs tracking-widest uppercase">Bonus picks are locked. Watch the tournament unfold.</div>
          </div>
        )}

        {/* Scoring policy + lock-date banner */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="p-4 rounded-2xl bg-lavender/5 border border-lavender/30 flex items-start gap-3" data-testid="bonus-disclaimer">
            <div className="text-lavender shrink-0 mt-0.5">⚖️</div>
            <div className="text-xs sm:text-sm text-white/70 leading-relaxed">
              <span className="text-lavender font-bold tracking-wider uppercase">Scoring policy ·</span>{" "}
              All bonus outcomes are awarded based on <span className="text-lavender font-semibold">official FIFA results</span>.
            </div>
          </div>
          <div className="p-4 rounded-2xl bg-gold/10 border border-gold/40 flex items-start gap-3" data-testid="bonus-lock-banner">
            <div className="text-gold shrink-0 mt-0.5">🔒</div>
            <div className="text-xs sm:text-sm text-white/80 leading-relaxed">
              <span className="text-gold font-bold tracking-wider uppercase">Picks lock ·</span>{" "}
              <span className="text-gold font-bold">Thursday, 18th June 2026 at 5:00 PM UAE time</span>.
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center text-white/40 py-20 text-sm tracking-widest uppercase">Loading…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {BONUS_DEFS.map((b) => {
              const meta = picksMeta[b.key];
              const result = results[b.key];
              const awarded = meta?.points > 0;
              const opts = options[b.key] || [];
              const catState = (config.categories || {})[b.key] || {};
              const categoryLocked = !!catState.is_locked || isLocked;
              return (
                <motion.div
                  key={b.key}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`pop-hover bg-surface border rounded-2xl p-5 ${
                    awarded ? "border-lavender/60 shadow-[0_0_20px_rgba(232,180,240,0.15)]" : "border-white/5"
                  }`}
                  data-testid={`bonus-card-${b.key}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        {b.key === "champion" ? <Trophy size={14} className="text-lavender"/> : <Star size={14} className="text-lavender"/>}
                        <div className="font-display text-xl uppercase tracking-wider">{b.label}</div>
                      </div>
                      <div className="text-[10px] tracking-widest uppercase text-white/40">{b.hint}</div>
                    </div>
                    <span className={`text-xs font-bold uppercase tracking-wider px-2 py-1 rounded-full ${awarded ? "bg-lavender/20 text-lavender border border-lavender/40" : "bg-purple/10 text-purple border border-purple/40"}`}>
                      {awarded ? `+${meta.points}` : `+${b.pts} pts`}
                    </span>
                  </div>

                  <select
                    value={picks[b.key] || ""}
                    onChange={(e) => setPicks({ ...picks, [b.key]: e.target.value })}
                    disabled={categoryLocked}
                    data-testid={`bonus-input-${b.key}`}
                    className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-purple focus:ring-1 focus:ring-purple outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <option value="">— pick {b.kind === "group" ? "a group" : b.kind === "player" ? "a player" : b.kind === "ou" ? "over/under" : "a country"} —</option>
                    {opts.map((o) => (
                      <option key={o} value={o}>{b.kind === "group" ? `Group ${o}` : o}</option>
                    ))}
                  </select>

                  {result ? (
                    <div className="mt-3 text-[10px] tracking-widest uppercase text-white/40">
                      Result: <span className="text-lavender">{result}</span>
                    </div>
                  ) : (
                    <div className="mt-3 text-[10px] tracking-widest uppercase text-white/40 inline-flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-purple animate-pulse" />
                      Awaiting result
                    </div>
                  )}

                  {categoryLocked ? (
                    <div className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-2.5 bg-lavender/10 border border-lavender/40 text-lavender text-xs font-bold uppercase tracking-wide" data-testid={`bonus-locked-${b.key}`}>
                      <Lock size={12}/> Locked
                    </div>
                  ) : (
                    <button
                      onClick={() => save(b.key)}
                      data-testid={`bonus-save-${b.key}`}
                      className="btn-bounce mt-4 w-full bg-gradient-to-r from-vivid to-purple text-white font-bold uppercase tracking-wide rounded-full px-6 py-2.5 hover:from-purple hover:to-lavender hover:text-plum transition-all text-xs"
                    >
                      {meta ? "Update Pick" : "Save Pick"}
                    </button>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
